import {
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  WarningAmberOutlined,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { Box, Chip, ChipProps, SvgIcon, Typography, IconButton, Collapse } from '@mui/material';
import { ExtendedList, ExtendedListItem, ExtendedListItemText, NestedList } from '@procureai/ui-mui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgQuoteComplianceCheck, SvbrgRiskScore } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { IntakePoliciesCheckResultParts } from '~/routes/IntakeDetail/steps/IntakeQuoteComplianceStep/components/QuoteComplianceDisplay/IntakePoliciesCheckResultParts';

interface QuoteComplianceDisplayProps {
  complianceResult: SvbrgQuoteComplianceCheck;
}

const propsByRiskMap: {
  [key in SvbrgRiskScore]: {
    color: ChipProps['color'];
    Icon: typeof SvgIcon;
    severity: 'success' | 'warning' | 'error';
    translationKey: string;
  };
} = {
  [SvbrgRiskScore.LOW]: {
    color: 'success',
    Icon: CheckCircleOutlined,
    severity: 'success',
    translationKey: 'low',
  },
  [SvbrgRiskScore.MEDIUM]: {
    color: 'warning',
    Icon: WarningAmberOutlined,
    severity: 'warning',
    translationKey: 'medium',
  },
  [SvbrgRiskScore.HIGH]: {
    color: 'error',
    Icon: ErrorOutlineOutlined,
    severity: 'error',
    translationKey: 'high',
  },
  [SvbrgRiskScore.UNKNOWN]: {
    color: 'default',
    Icon: WarningAmberOutlined,
    severity: 'warning',
    translationKey: 'unknown',
  },
};

export const QuoteComplianceDisplay = ({ complianceResult }: QuoteComplianceDisplayProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeQuoteComplianceStep' });
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const { score, userComment, userReason } = complianceResult;
  const { color, Icon, translationKey } = propsByRiskMap[score] || propsByRiskMap[SvbrgRiskScore.LOW];

  const hasParts = complianceResult.complianceData?.parts && complianceResult.complianceData.parts.length > 0;

  if (!complianceResult.complianceData) {
    return null;
  }

  return (
    <ExtendedList>
      {/* Main compliance result as first list item */}
      <ExtendedListItem>
        <ExtendedListItemText
          primary={t('complianceResult.quoteFileName')}
          secondary={complianceResult.complianceData.summary}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={t(`riskLevel.${translationKey}`)}
            color={color}
            variant="outlined"
            size="small"
            icon={<Icon />}
          />
          {hasParts && (
            <IconButton
              size="small"
              onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
              aria-label={isDetailsExpanded ? 'Collapse details' : 'Expand details'}
            >
              {isDetailsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>
      </ExtendedListItem>

      {/* Detailed compliance breakdown in collapsible nested list */}
      {hasParts && (
        <Collapse in={isDetailsExpanded}>
          <NestedList>
            <IntakePoliciesCheckResultParts result={complianceResult.complianceData} fileName="" />
          </NestedList>
        </Collapse>
      )}

      {/* Additional details if present */}
      {(userComment || userReason) && (
        <Box sx={{ mt: 2, px: 2 }}>
          {userComment && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('complianceResult.comments')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userComment}
              </Typography>
            </Box>
          )}

          {userReason && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('complianceResult.reason')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userReason}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </ExtendedList>
  );
};
